import React from 'react';
import style from './mobileStickyFooter.module.scss';
import classNames from 'classnames';
import { NavLink } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { newFlightRequest } from 'acscp/components/routeHelper';
import { PresentationComponentBase } from 'acscp/components/presentationComponentBase';
import { AppState, Menu } from 'acscp/store';
import { Dispatch } from 'redux';
import {
    userCanAccessCharters,
    userCanOnlyAccessLoyalty,
    userCanPotentiallyRequestFlights,
    userIsAuthenticated,
    userIsLoggedIn
} from 'acscp/store/helpers/authHelper';
import { MyChartersIcon } from '../icons/myChartersIcon';
import { HomeIcon } from '../icons/homeIcon';
import { PlaneIcon } from '../icons/planeIcon';
import { NotificationIcon } from '../icons/notificationIcon';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { getNumberOfUnreadNotifications } from 'acscp/store/selectors/accountNotificationSelector';
import { FetchRequest, SetOpenMenu } from 'acscp/actions';
import { AccountNotificationsRequest } from 'acscp/api/requests';

interface MobileStickyFooterStateProps {
    userIsLoggedIn: boolean;
    isAuthenticated: boolean;
    canOnlyAccessLoyalty: boolean;
    canAccessCharters: boolean;
    canPotentiallyRequestFlights: boolean;
    numberOfUnreadNotifications: number;
    areNotificationsOpen: boolean;
}

interface MobileStickyFooterDispatchProps {
    fetchNotifications: () => void;
    openAccountNotificationsMenu: () => void;
    closeMenu: () => void;
}

interface MobileStickyFooterInternalState {
    isShowing: boolean;
}

type MobileStickyFooterProps = MobileStickyFooterStateProps & MobileStickyFooterDispatchProps;

export class MobileStickyFooterPresentational extends PresentationComponentBase<
    {}, MobileStickyFooterStateProps, MobileStickyFooterDispatchProps, MobileStickyFooterInternalState> {

    constructor(props: MobileStickyFooterProps) {
        super(props);

        this.state = {
            isShowing: true
        };
    }

    show = (): void => {
        this.setState({isShowing: true});
    }

    hide = (): void => {
        this.setState({isShowing: false});
    }

    componentDidMount(): void {
        // These events don't fire on Android; see https://github.com/cjpearson/cordova-plugin-keyboard/issues/48
        window.addEventListener('keyboardWillShow', this.hide);
        window.addEventListener('keyboardWillHide', this.show);

        if (this.props.isAuthenticated) {
            this.props.fetchNotifications();
        }
    }

    componentWillUnmount(): void {
        window.removeEventListener('keyboardWillShow', this.hide);
        window.removeEventListener('keyboardWillHide', this.show);
    }

    componentDidUpdate(prevProps: MobileStickyFooterProps): void {
        if (!prevProps.isAuthenticated && this.props.isAuthenticated) {
            this.props.fetchNotifications();
        }
    }

    toggleAccountNotifications = () => {
        this.props.areNotificationsOpen ? this.props.closeMenu() : this.props.openAccountNotificationsMenu();
    }

    render(): JSX.Element | null {
        if (!ENABLE_CREATE_ACCOUNT && !this.props.userIsLoggedIn) {
            // If the user is not logged in, do not display the footer
            // (since there's no way for them to create an account after clicking one of the options)
            return null;
        }

        if (this.props.canOnlyAccessLoyalty) {
            // If user is logged in, but does not have the ability to access charters do not render the footer
            // This is as it will only have one item with a home link.
            return null;
        }

        if (!this.state.isShowing) {
            return null;
        }

        const unreadNotifications = this.props.numberOfUnreadNotifications;
        const hasUnreadNotifications = unreadNotifications > 0;
        return (
            <div className={style.StickyFooter}>
                <div className={style.Menu}>
                    <NavLink
                        to={CHARTERS_HOME}
                        className={style.MenuItem}
                        activeClassName={style.Selected}
                        onClick={this.props.closeMenu}
                    >
                        <div className={style.Icon}>
                            <HomeIcon />
                        </div>
                        <FormattedMessage id="components.mobileStickyFooter.home" defaultMessage="HOME" />
                    </NavLink>
                    {this.props.canAccessCharters &&
                        <NavLink
                            to="/trip"
                            className={style.MenuItem}
                            activeClassName={style.Selected}
                            onClick={this.props.closeMenu}
                        >
                            <div className={style.Icon}>
                                <MyChartersIcon />
                            </div>
                            <FormattedMessage id="components.mobileStickyFooter.myCharters" defaultMessage="MY CHARTERS" />
                        </NavLink>
                    }
                    {this.props.canPotentiallyRequestFlights &&
                        <NavLink
                            to={newFlightRequest()}
                            className={style.MenuItem}
                            activeClassName={style.Selected}
                            onClick={this.props.closeMenu}
                        >
                            <div className={style.Icon}>
                                <PlaneIcon rotate={-45} />
                            </div>
                            <FormattedMessage id="components.mobileStickyFooter.newEnquiry" defaultMessage="NEW ENQUIRY" />
                        </NavLink>
                    }
                    {this.props.isAuthenticated &&
                        <div
                            className={classNames(
                                style.Notifications,
                                style.MenuItem,
                                {
                                    [style.HasUnreadNotifications]: hasUnreadNotifications
                                })
                            }
                            onClick={this.toggleAccountNotifications}
                        >
                            <div className={style.Icon}>
                                <NotificationIcon />
                            </div>
                            <div
                                className={classNames(
                                    style.NotificationNumber,
                                    hasUnreadNotifications && style.HasUnreadNotifications
                                )}
                            >
                                {unreadNotifications > 99 ? '99+' : unreadNotifications}
                            </div>
                            <FormattedMessage
                                id={'components.mobileStickyFooter.notifications'}
                                defaultMessage={'NOTIFICATIONS'}
                            />
                        </div>
                    }
                </div>
            </div>
        );
    }
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: {}): MobileStickyFooterDispatchProps  {
    return {
        fetchNotifications: () => dispatch(new FetchRequest(new AccountNotificationsRequest())),
        openAccountNotificationsMenu: () => dispatch(new SetOpenMenu(Menu.AccountNotifications)),
        closeMenu: () => dispatch(new SetOpenMenu(undefined))
    };
}

function mapStateToProps(state: AppState, ownProps: {}): MobileStickyFooterStateProps {
    return {
        userIsLoggedIn: userIsLoggedIn(state.auth),
        isAuthenticated: userIsAuthenticated(state.auth),
        canAccessCharters: userCanAccessCharters(state.auth),
        canOnlyAccessLoyalty: userCanOnlyAccessLoyalty(state.auth),
        canPotentiallyRequestFlights: userCanPotentiallyRequestFlights(state.auth),
        numberOfUnreadNotifications: getNumberOfUnreadNotifications(state),
        areNotificationsOpen: state.menu.currentlyOpenMenu === Menu.AccountNotifications
    };
}

export const MobileStickyFooter =
    connectWithRequests(mapStateToProps, mapDispatchToProps)(MobileStickyFooterPresentational);
